import { useState, useEffect, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Plus, Minus, ArrowLeft, ArrowRight, ArrowUp, ArrowDown, ArrowDownToLine, ArrowUpFromLine, RefreshCw, Hand, Cake, Coins, Target, Sprout, Mountain, Check, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Sparkles, TriangleAlert, LogOut, ChevronDown, ChevronRight, X, Banknote, ChartColumn, Lightbulb, Search, House, Compass, ScrollText, Settings as SettingsIcon } from "lucide-react";
import { Logo, BrandMark } from "@/components/Brand";
import { BadgeShelf, NewBadgesBanner } from "@/components/Badges";
import { StockDirectory } from "@/components/StockDirectory";
import { GlobalSearch } from "@/components/GlobalSearch";
import { CategoryIcon } from "@/components/CategoryIcon";
import { WatchlistProvider, WatchlistCorral, StarButton } from "@/components/Watchlist";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to use token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses by clearing auth
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
    }
    return Promise.reject(error);
  }
);

const RichText = ({ text }) => (
  <>{(text || '').replace(/^#+\s*/gm, '').replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, '').split('**').map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part))}</>
);

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, token) => {
    if (token) {
      try {
        localStorage.setItem('auth_token', token);
      } catch (e) {
        console.error('Failed to save token:', e);
      }
    }
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error(e);
    }
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.onboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Loading Screen
const LoadingScreen = () => (
  <div className="loading-screen" data-testid="loading-screen">
    <div className="loading-logo"><Logo size={84} /></div>
    <h1 className="loading-title">Stock Rodeo</h1>
    <p className="loading-subtitle">Grow Your Knowledge!</p>
    <div className="loading-spinner"></div>
  </div>
);

// Auth Callback for Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = hash.split('session_id=')[1]?.split('&')[0];

      if (sessionId) {
        try {
          const response = await axios.post(
            `${API}/auth/session`,
            { session_id: sessionId }
          );
          login(response.data, response.data.token);
          
          if (response.data.onboarded) {
            navigate('/dashboard', { replace: true, state: { user: response.data } });
          } else {
            navigate('/onboarding', { replace: true, state: { user: response.data } });
          }
        } catch (error) {
          console.error('Auth callback error:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [login, navigate]);

  return <LoadingScreen />;
};

// Login Page
const LoginPage = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, user } = useAuth();

  // Clear any stale auth data on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token && !user) {
      // There's a token but no user - might be stale, clear it
      localStorage.removeItem('auth_token');
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      if (user.onboarded) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // Clear any stale data before Google login
    localStorage.removeItem('auth_token');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleClearAndRetry = () => {
    localStorage.removeItem('auth_token');
    setError('');
    setEmail('');
    setPassword('');
    setName('');
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Clear any stale token before login attempt
    localStorage.removeItem('auth_token');

    try {
      if (isRegister) {
        const response = await axios.post(`${API}/auth/register`, { email, password, name });
        login(response.data, response.data.token);
        navigate('/onboarding', { replace: true });
      } else {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        login(response.data, response.data.token);
        if (response.data.onboarded) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/onboarding', { replace: true });
        }
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Something went wrong. Try clearing and retrying.';
      if (errorMsg === 'Email already registered') {
        setError('This email is already registered. Try signing in instead.');
      } else if (errorMsg === 'Invalid credentials') {
        setError('Invalid email or password. Please try again.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="login-page">
      <div className="auth-container">
        <div className="auth-logo" data-testid="auth-logo"><Logo size={92} /></div>
        <h1 className="auth-title">Stock Rodeo</h1>
        <p className="auth-subtitle">Learn to invest with fake money!</p>

        <button className="google-btn" onClick={handleGoogleLogin} data-testid="google-login-btn">
          <svg className="google-icon" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {isRegister && (
            <input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="auth-input"
              data-testid="register-name-input"
              required
            />
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input"
            data-testid="email-input"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
            data-testid="password-input"
            required
          />
          
          {error && (
            <div className="auth-error-container">
              <p className="auth-error" data-testid="auth-error">{error}</p>
              <button type="button" className="clear-retry-btn" onClick={handleClearAndRetry}>
                Clear & Retry
              </button>
            </div>
          )}
          
          <button type="submit" className="auth-submit-btn" disabled={loading} data-testid="auth-submit-btn">
            {loading ? 'Loading...' : (isRegister ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        <p className="auth-switch">
          {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button onClick={() => setIsRegister(!isRegister)} className="auth-switch-btn" data-testid="auth-switch-btn">
            {isRegister ? 'Sign In' : 'Create Account'}
          </button>
        </p>
      </div>
    </div>
  );
};

// Onboarding Page
const OnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [startingBalance, setStartingBalance] = useState('10000');
  const [complexityMode, setComplexityMode] = useState('simple');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  const handleNext = () => {
    if (step === 1 && !name.trim()) {
      alert('Please enter your name');
      return;
    }
    if (step === 2 && (!age || parseInt(age) < 5 || parseInt(age) > 99)) {
      alert('Please enter a valid age');
      return;
    }
    if (step === 3 && (!startingBalance || parseFloat(startingBalance) < 100)) {
      alert('Starting balance should be at least $100');
      return;
    }
    if (step < 4) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/users/onboard`, {
        name: name.trim(),
        age: parseInt(age),
        starting_balance: parseFloat(startingBalance),
        complexity_mode: complexityMode
      });
      
      setUser(response.data);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Onboarding error:', error);
      alert('Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="step-container" data-testid="onboarding-step-1">
            <div className="step-emoji"><Hand size={44} /></div>
            <h2 className="step-title">What's your name?</h2>
            <p className="step-subtitle">Let's get to know you!</p>
            <input
              type="text"
              className="onboarding-input"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="onboarding-name-input"
            />
          </div>
        );
      case 2:
        return (
          <div className="step-container" data-testid="onboarding-step-2">
            <div className="step-emoji"><Cake size={44} /></div>
            <h2 className="step-title">How old are you?</h2>
            <p className="step-subtitle">This helps us personalize your experience</p>
            <input
              type="number"
              className="onboarding-input"
              placeholder="Enter your age"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              min="5"
              max="99"
              autoFocus
              data-testid="onboarding-age-input"
            />
          </div>
        );
      case 3:
        return (
          <div className="step-container" data-testid="onboarding-step-3">
            <div className="step-emoji"><Coins size={44} /></div>
            <h2 className="step-title">Starting Balance</h2>
            <p className="step-subtitle">How much fake money do you want to start with?</p>
            <div className="balance-input-container">
              <span className="dollar-sign">$</span>
              <input
                type="number"
                className="balance-input"
                placeholder="10000"
                value={startingBalance}
                onChange={(e) => setStartingBalance(e.target.value)}
                autoFocus
                data-testid="onboarding-balance-input"
              />
            </div>
            <div className="quick-amounts">
              {['1000', '5000', '10000', '50000'].map((amount) => (
                <button
                  key={amount}
                  className={`quick-amount ${startingBalance === amount ? 'active' : ''}`}
                  onClick={() => setStartingBalance(amount)}
                  data-testid={`quick-amount-${amount}`}
                >
                  ${parseInt(amount).toLocaleString()}
                </button>
              ))}
            </div>
          </div>
        );
      case 4:
        return (
          <div className="step-container" data-testid="onboarding-step-4">
            <div className="step-emoji"><Target size={44} /></div>
            <h2 className="step-title">Experience Level</h2>
            <p className="step-subtitle">Choose how detailed you want the info</p>
            <div className="mode-options">
              <button
                className={`mode-card ${complexityMode === 'simple' ? 'active' : ''}`}
                onClick={() => setComplexityMode('simple')}
                data-testid="mode-simple"
              >
                <div className="mode-header">
                  <span className="mode-emoji"><Sprout size={26} /></span>
                  <span className="mode-title">Simple Mode</span>
                  {complexityMode === 'simple' && <span className="mode-check"><Check size={18} /></span>}
                </div>
                <p className="mode-description">Easy explanations, fun learning experience. Perfect for beginners!</p>
                <p className="mode-age">Recommended: Ages 8-12</p>
              </button>
              <button
                className={`mode-card ${complexityMode === 'advanced' ? 'active' : ''}`}
                onClick={() => setComplexityMode('advanced')}
                data-testid="mode-advanced"
              >
                <div className="mode-header">
                  <span className="mode-emoji"><Mountain size={26} /></span>
                  <span className="mode-title">Advanced Mode</span>
                  {complexityMode === 'advanced' && <span className="mode-check"><Check size={18} /></span>}
                </div>
                <p className="mode-description">More detailed info with real trading terms. For serious learners!</p>
                <p className="mode-age">Recommended: Ages 13+</p>
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-page" data-testid="onboarding-page">
      <div className="onboarding-container">
        <div className="progress-dots">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`progress-dot ${s <= step ? 'active' : ''}`} />
          ))}
        </div>

        {renderStep()}

        <div className="onboarding-buttons">
          {step > 1 && (
            <button className="back-btn" onClick={() => setStep(step - 1)} data-testid="onboarding-back-btn">
              <ArrowLeft size={20} />
            </button>
          )}
          <button
            className="next-btn"
            onClick={handleNext}
            disabled={loading}
            data-testid="onboarding-next-btn"
          >
            {loading ? 'Loading...' : <>{step === 4 ? "Let's Go!" : 'Next'} <ArrowRight size={18} /></>}
          </button>
        </div>
      </div>
    </div>
  );
};

// Layout with Navigation
const Layout = ({ children, activeTab }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const tabs = [
    { id: 'dashboard', Icon: House, label: 'Home', path: '/dashboard' },
    { id: 'explore', Icon: Compass, label: 'Explore', path: '/explore' },
    { id: 'history', Icon: ScrollText, label: 'History', path: '/history' },
    { id: 'settings', Icon: SettingsIcon, label: 'Settings', path: '/settings' },
  ];

  return (
    <div className="app-layout" data-testid="app-layout">
      <header className="top-bar" data-testid="top-bar">
        <button className="top-bar-brand" onClick={() => navigate('/dashboard')} data-testid="top-bar-home">
          <BrandMark />
        </button>
        <GlobalSearch />
        <span className="top-bar-user" data-testid="top-bar-user">{user?.name}</span>
      </header>
      <main className="main-content">{children}</main>
      <nav className="bottom-nav" data-testid="bottom-nav">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => navigate(tab.path)}
            data-testid={`nav-${tab.id}`}
          >
            <span className="nav-icon"><tab.Icon size={22} strokeWidth={2} /></span>
            <span className="nav-label">{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/${user.user_id}`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.user_id) fetchDashboard();
  }, [user]);

  const formatMoney = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) return <Layout activeTab="dashboard"><LoadingScreen /></Layout>;

  return (
    <Layout activeTab="dashboard">
      <div className="dashboard-page" data-testid="dashboard-page">
        <div className="dashboard-header">
          <div>
            <p className="greeting">Welcome back,</p>
            <h1 className="user-name">{dashboard?.user?.name || user?.name}</h1>
          </div>
          <div className="mode-tag">
            {dashboard?.user?.complexity_mode === 'advanced' ? 'Trail Pro' : 'Greenhorn'}
          </div>
        </div>

        <div className="total-card" data-testid="total-card">
          <p className="total-label">Total Account Value</p>
          <h2 className="total-value">{formatMoney(dashboard?.total_account_value)}</h2>
          <div className={`profit-badge ${(dashboard?.total_profit_loss || 0) >= 0 ? 'positive' : 'negative'}`}>
            <span className="profit-icon">{(dashboard?.total_profit_loss || 0) >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}</span>
            <span className="profit-text">
              {formatMoney(Math.abs(dashboard?.total_profit_loss || 0))} ({(dashboard?.total_profit_loss_percent || 0).toFixed(2)}%)
            </span>
          </div>
        </div>

        <div className="balance-row">
          <div className="balance-card" data-testid="cash-balance-card">
            <span className="balance-icon"><Banknote size={22} /></span>
            <p className="balance-label">Cash</p>
            <p className="balance-value">{formatMoney(dashboard?.cash_balance)}</p>
          </div>
          <div className="balance-card" data-testid="invested-balance-card">
            <span className="balance-icon"><ChartColumn size={22} /></span>
            <p className="balance-label">Invested</p>
            <p className="balance-value">{formatMoney(dashboard?.portfolio_value)}</p>
          </div>
        </div>

        <NewBadgesBanner badges={dashboard?.new_badges} />
        <WatchlistCorral />
        <BadgeShelf badges={dashboard?.badges} />

        <div className="section-header">
          <h3 className="section-title">Your Stocks</h3>
          <button className="add-more-btn" onClick={() => navigate('/explore')} data-testid="add-more-btn">+ Add More</button>
        </div>

        {(!dashboard?.portfolio || dashboard.portfolio.length === 0) ? (
          <div className="empty-card" data-testid="empty-portfolio">
            <span className="empty-emoji"><ChartColumn size={40} /></span>
            <h4 className="empty-title">No stocks yet!</h4>
            <p className="empty-subtitle">Start investing to build your portfolio</p>
            <button className="explore-btn" onClick={() => navigate('/explore')} data-testid="explore-btn">
              Explore Stocks
            </button>
          </div>
        ) : (
          <div className="portfolio-list" data-testid="portfolio-list">
            {dashboard.portfolio.map((item) => (
              <div
                key={item.id}
                className="stock-card"
                onClick={() => navigate(`/stock/${item.symbol}`)}
                data-testid={`stock-${item.symbol}`}
              >
                <div className="stock-left">
                  <div className="stock-symbol-badge">
                    <span>{item.symbol}</span>
                  </div>
                  <div className="stock-info">
                    <p className="stock-name">{item.company_name}</p>
                    <p className="stock-shares">{item.quantity} shares</p>
                  </div>
                </div>
                <div className="stock-right">
                  <p className="stock-value">{formatMoney(item.current_value)}</p>
                  <div className={`stock-change ${item.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                    {item.profit_loss >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                    <span>{item.profit_loss_percent.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="tip-card" data-testid="tip-card">
          <span className="tip-icon"><Lightbulb size={22} /></span>
          <div className="tip-content">
            <h4 className="tip-title">Investing Tip</h4>
            <p className="tip-text">
              Don't put all your money in one stock. Diversify by investing in different companies!
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Explore Page
const ExplorePage = () => {
  const [categories, setCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/stocks/categories`);
      setCategories(response.data.categories || []);
      
      const initialData = {};
      response.data.categories.forEach((cat) => {
        initialData[cat.id] = {
          stocks: [],
          loading: false,
          loadingMore: false,
          offset: 0,
          hasMore: true,
          expanded: false,
        };
      });
      setCategoryData(initialData);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = async (categoryId) => {
    const currentData = categoryData[categoryId];
    const newExpanded = !currentData.expanded;

    setCategoryData((prev) => ({
      ...prev,
      [categoryId]: { ...prev[categoryId], expanded: newExpanded },
    }));

    if (newExpanded && currentData.stocks.length === 0) {
      await loadCategoryStocks(categoryId, 0);
    }
  };

  const loadCategoryStocks = async (categoryId, offset) => {
    const isInitialLoad = offset === 0;

    setCategoryData((prev) => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        loading: isInitialLoad,
        loadingMore: !isInitialLoad,
      },
    }));

    try {
      const response = await axios.get(`${API}/stocks/category/${categoryId}?limit=12&offset=${offset}`);

      setCategoryData((prev) => ({
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          stocks: isInitialLoad
            ? response.data.stocks
            : [...prev[categoryId].stocks, ...response.data.stocks],
          offset: response.data.offset + response.data.limit,
          hasMore: response.data.has_more,
          loading: false,
          loadingMore: false,
        },
      }));
    } catch (error) {
      console.error(`Error loading stocks for ${categoryId}:`, error);
      setCategoryData((prev) => ({
        ...prev,
        [categoryId]: { ...prev[categoryId], loading: false, loadingMore: false },
      }));
    }
  };

  const searchSeq = useRef(0);
  const searchTimer = useRef(null);

  const handleSearch = (query) => {
    setSearchQuery(query);
    clearTimeout(searchTimer.current);
    const seq = ++searchSeq.current;
    if (query.trim().length < 1) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/stocks/search/${encodeURIComponent(query.trim())}`);
        if (seq === searchSeq.current) setSearchResults(response.data.results || []);
      } catch (error) {
        console.error('Error searching stocks:', error);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 300);
  };

  const formatMoney = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const StockCard = ({ stock }) => (
    <div className="explore-stock-card" onClick={() => navigate(`/stock/${stock.symbol}`)} data-testid={`explore-stock-${stock.symbol}`}>
      <div className="stock-left">
        <div className="stock-icon">{stock.symbol.charAt(0)}</div>
        <div className="stock-info">
          <p className="stock-symbol">{stock.symbol}</p>
          <p className="stock-name">{stock.name}</p>
        </div>
      </div>
      <StarButton symbol={stock.symbol} />
      <div className="stock-right">
        <p className="stock-price">{stock.price == null ? '—' : formatMoney(stock.price)}</p>
        <div className={`stock-change ${stock.change_percent >= 0 ? 'positive' : 'negative'}`}>
          {stock.change_percent >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          <span>{Math.abs(stock.change_percent).toFixed(2)}%</span>
        </div>
      </div>
    </div>
  );

  if (loading) return <Layout activeTab="explore"><LoadingScreen /></Layout>;

  return (
    <Layout activeTab="explore">
      <div className="explore-page" data-testid="explore-page">
        <div className="explore-header">
          <h1 className="explore-title">Explore Stocks</h1>
          <p className="explore-subtitle">Discover companies by category</p>
        </div>

        <div className="search-container">
          <span className="search-icon"><Search size={18} /></span>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or symbol..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            data-testid="search-input"
          />
          {searchQuery && (
            <button className="search-clear" data-testid="search-clear-btn" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
              <X size={16} />
            </button>
          )}
        </div>

        {searchQuery.length > 0 ? (
          <div className="search-results" data-testid="search-results">
            <h3 className="section-title">Search Results</h3>
            {searching ? (
              <div className="loading-spinner small"></div>
            ) : searchResults.length === 0 ? (
              <div className="empty-search">
                <p>No stocks found for "{searchQuery}"</p>
              </div>
            ) : (
              searchResults.map((stock) => <StockCard key={stock.symbol} stock={stock} />)
            )}
          </div>
        ) : (
          <div className="categories-list" data-testid="categories-list">
            {categories.map((category) => {
              const data = categoryData[category.id] || {};
              return (
                <div key={category.id} className="category-container">
                  <button className="category-header" onClick={() => toggleCategory(category.id)} data-testid={`category-${category.id}`}>
                    <div className="category-left">
                      <span className="category-emoji"><CategoryIcon id={category.id} /></span>
                      <div>
                        <p className="category-name">{category.name}</p>
                        <p className="category-count">{category.total_stocks} stocks</p>
                      </div>
                    </div>
                    <span className="category-arrow">{data.expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                  </button>

                  {data.expanded && (
                    <div className="category-content">
                      {data.loading ? (
                        <div className="loading-spinner small"></div>
                      ) : (
                        <>
                          <p className="category-description">{category.description}</p>
                          {data.stocks.map((stock) => (
                            <StockCard key={stock.symbol} stock={stock} />
                          ))}
                          {data.hasMore && (
                            <button
                              className="load-more-btn"
                              onClick={() => loadCategoryStocks(category.id, data.offset)}
                              disabled={data.loadingMore}
                              data-testid={`load-more-${category.id}`}
                            >
                              {data.loadingMore ? 'Loading...' : '+ Load More Stocks'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {searchQuery.length === 0 && <StockDirectory />}

        <div className="edu-card">
          <span className="edu-emoji"><Sprout size={30} /></span>
          <h4 className="edu-title">Growing Your Portfolio</h4>
          <p className="edu-text">
            A portfolio is your collection of stocks. Try investing in different categories to spread out your risk - this is called diversification!
          </p>
        </div>
      </div>
    </Layout>
  );
};

// Stock Detail Page
const StockDetailPage = () => {
  const [stock, setStock] = useState(null);
  const [history, setHistory] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('1mo');
  const [hoverData, setHoverData] = useState(null);
  const chartRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const symbol = location.pathname.split('/stock/')[1];

  const periods = [
    { label: '1D', value: '1d' },
    { label: '5D', value: '5d' },
    { label: '1M', value: '1mo' },
    { label: '3M', value: '3mo' },
    { label: '6M', value: '6mo' },
    { label: '1Y', value: '1y' },
  ];

  useEffect(() => {
    fetchData();
  }, [symbol]);

  useEffect(() => {
    fetchHistory();
  }, [selectedPeriod, symbol]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const [stockRes, portfolioRes] = await Promise.all([
        axios.get(`${API}/stocks/${symbol}`),
        user ? axios.get(`${API}/portfolio/${user.user_id}`) : Promise.resolve(null),
      ]);

      setStock(stockRes.data);

      if (portfolioRes?.data) {
        const holding = portfolioRes.data.portfolio.find((p) => p.symbol === symbol);
        setPortfolio(holding || null);
      }
    } catch (error) {
      setStock(null);
      setLoadError(error.response?.status === 503 ? error.response.data.detail : 'Stock not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/stocks/${symbol}/history?period=${selectedPeriod}`);
      setHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const formatMoney = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatVolume = (value) => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value.toString();
  };

  if (loading) return <Layout activeTab="explore"><LoadingScreen /></Layout>;
  if (!stock) return (
    <Layout activeTab="explore">
      <div className="error-page" data-testid="stock-error">
        <p>{loadError || 'Stock not found'}</p>
        <button className="pager-btn" onClick={fetchData} data-testid="stock-retry-btn"><RefreshCw size={15} /> Try again</button>
      </div>
    </Layout>
  );

  return (
    <Layout activeTab="explore">
      <div className="stock-detail-page" data-testid="stock-detail-page">
        <button className="back-btn-top" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft size={16} /> Back</button>

        <div className="stock-header">
          <div>
            <h1 className="stock-symbol-title">{stock.symbol} <StarButton symbol={stock.symbol} size={24} className="star-lg" /></h1>
            <p className="stock-name-subtitle">{stock.name}</p>
          </div>
          <div className="sector-tag">{stock.sector}</div>
        </div>

        <div className="price-section">
          <h2 className="price">{formatMoney(stock.price)}</h2>
          <div className={`change-badge ${stock.change_percent >= 0 ? 'positive' : 'negative'}`}>
            {stock.change_percent >= 0 ? <ArrowUp size={15} /> : <ArrowDown size={15} />}
            <span>{formatMoney(Math.abs(stock.change))} ({Math.abs(stock.change_percent).toFixed(2)}%)</span>
          </div>
        </div>

        <div className="chart-container" data-testid="chart-container">
          {history.length > 0 ? (
            (() => {
              const prices = history.map((d) => d.close);
              const dates = history.map((d) => d.date);
              const min = Math.min(...prices);
              const max = Math.max(...prices);
              const range = max - min || 1;
              const narrow = window.innerWidth < 640;
              const padding = narrow ? { top: 16, right: 12, bottom: 36, left: 58 } : { top: 20, right: 20, bottom: 45, left: 80 };
              const chartWidth = narrow ? 420 : 850;
              const chartHeight = narrow ? 300 : 300;
              const innerWidth = chartWidth - padding.left - padding.right;
              const innerHeight = chartHeight - padding.top - padding.bottom;
              
              const getX = (i) => padding.left + (i / (prices.length - 1)) * innerWidth;
              const getY = (p) => padding.top + innerHeight - ((p - min) / range) * innerHeight;
              
              const points = prices.map((p, i) => `${getX(i)},${getY(p)}`).join(' ');
              const areaPoints = `${padding.left},${padding.top + innerHeight} ${points} ${padding.left + innerWidth},${padding.top + innerHeight}`;
              
              // Y-axis labels (5 values)
              const yLabels = [];
              for (let i = 0; i <= 4; i++) {
                const value = min + (range * i) / 4;
                yLabels.push({ value, y: getY(value) });
              }
              
              // X-axis labels (5 dates)
              const xLabels = [];
              const step = Math.floor(dates.length / 4);
              for (let i = 0; i < dates.length; i += step) {
                if (xLabels.length < 5) {
                  const date = new Date(dates[i]);
                  xLabels.push({ 
                    label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), 
                    x: getX(i) 
                  });
                }
              }
              
              const handleMouseMove = (e) => {
                if (!chartRef.current) return;
                const rect = chartRef.current.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                const svgX = (x / rect.width) * chartWidth;
                
                if (svgX >= padding.left && svgX <= chartWidth - padding.right) {
                  const index = Math.round(((svgX - padding.left) / innerWidth) * (prices.length - 1));
                  if (index >= 0 && index < prices.length) {
                    setHoverData({
                      price: prices[index],
                      date: dates[index],
                      x: getX(index),
                      y: getY(prices[index]),
                      mouseX: x,
                      mouseY: y,
                      index
                    });
                  }
                }
              };
              
              const handleMouseLeave = () => setHoverData(null);
              
              return (
                <div className="interactive-chart">
                  {hoverData && (
                    <div className="chart-tooltip" style={{ left: hoverData.mouseX + 15, top: hoverData.mouseY - 30 }}>
                      <span className="tooltip-price">${hoverData.price.toFixed(2)}</span>
                      <span className="tooltip-date">{new Date(hoverData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                  )}
                  <svg 
                    ref={chartRef}
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`} 
                    preserveAspectRatio="xMidYMid meet"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    onTouchMove={(e) => {
                      const touch = e.touches[0];
                      handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
                    }}
                    onTouchEnd={handleMouseLeave}
                  >
                    <defs>
                      <linearGradient id="chartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={stock.change_percent >= 0 ? '#4ade80' : '#f87171'} stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#0a0a1a" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    
                    {/* Grid lines */}
                    {yLabels.map((label, i) => (
                      <line key={i} x1={padding.left} y1={label.y} x2={chartWidth - padding.right} y2={label.y} stroke="#1e293b" strokeWidth="1" />
                    ))}
                    
                    {/* Y-axis labels */}
                    {yLabels.map((label, i) => (
                      <text key={i} x={padding.left - 8} y={label.y + 4} fill="#94a3b8" fontSize="12" textAnchor="end" fontFamily="sans-serif">
                        {'$' + label.value.toFixed(0)}
                      </text>
                    ))}
                    
                    {/* X-axis labels */}
                    {xLabels.map((label, i) => (
                      <text key={i} x={label.x} y={chartHeight - 15} fill="#94a3b8" fontSize="11" textAnchor="middle">
                        {label.label}
                      </text>
                    ))}
                    
                    {/* Chart area fill */}
                    <polygon fill="url(#chartGradient)" points={areaPoints} />
                    
                    {/* Chart line */}
                    <polyline
                      fill="none"
                      stroke={stock.change_percent >= 0 ? '#4ade80' : '#f87171'}
                      strokeWidth="2"
                      points={points}
                    />
                    
                    {/* Hover indicator */}
                    {hoverData && (
                      <>
                        <line x1={hoverData.x} y1={padding.top} x2={hoverData.x} y2={padding.top + innerHeight} stroke="#4ade80" strokeWidth="1" strokeDasharray="4,4" />
                        <circle cx={hoverData.x} cy={hoverData.y} r="6" fill={stock.change_percent >= 0 ? '#4ade80' : '#f87171'} stroke="#fff" strokeWidth="2" />
                      </>
                    )}
                    
                    {/* Chart border */}
                    <rect x={padding.left} y={padding.top} width={innerWidth} height={innerHeight} fill="none" stroke="#334155" strokeWidth="1" />
                  </svg>
                </div>
              );
            })()
          ) : (
            <div className="no-chart">No chart data available</div>
          )}
        </div>

        <div className="period-selector">
          {periods.map((period) => (
            <button
              key={period.value}
              className={`period-btn ${selectedPeriod === period.value ? 'active' : ''}`}
              onClick={() => setSelectedPeriod(period.value)}
              data-testid={`period-${period.value}`}
            >
              {period.label}
            </button>
          ))}
        </div>

        {portfolio && (
          <div className="holdings-card" data-testid="holdings-card">
            <h3 className="holdings-title">Your Holdings</h3>
            <div className="holdings-grid">
              <div className="holding-item">
                <span className="holding-label">Shares</span>
                <span className="holding-value">{portfolio.quantity}</span>
              </div>
              <div className="holding-item">
                <span className="holding-label">Avg. Cost</span>
                <span className="holding-value">{formatMoney(portfolio.avg_buy_price)}</span>
              </div>
              <div className="holding-item">
                <span className="holding-label">Value</span>
                <span className="holding-value">{formatMoney(portfolio.current_value)}</span>
              </div>
              <div className="holding-item">
                <span className="holding-label">Profit/Loss</span>
                <span className={`holding-value ${portfolio.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                  {portfolio.profit_loss >= 0 ? '+' : ''}{formatMoney(portfolio.profit_loss)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-label">Open</span>
            <span className="stat-value">{stock.open ? formatMoney(stock.open) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Previous Close</span>
            <span className="stat-value">{stock.previous_close ? formatMoney(stock.previous_close) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Day High</span>
            <span className="stat-value">{stock.high ? formatMoney(stock.high) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Day Low</span>
            <span className="stat-value">{stock.low ? formatMoney(stock.low) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">52W High</span>
            <span className="stat-value">{stock['52_week_high'] ? formatMoney(stock['52_week_high']) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">52W Low</span>
            <span className="stat-value">{stock['52_week_low'] ? formatMoney(stock['52_week_low']) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Volume</span>
            <span className="stat-value">{stock.volume ? formatVolume(stock.volume) : "—"}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Market Cap</span>
            <span className="stat-value">{stock.market_cap || '—'}</span>
          </div>
        </div>

        {stock.description && (
          <div className="about-section">
            <h3 className="about-title">About {stock.name}</h3>
            <p className="about-text">{stock.description}</p>
            <div className="industry-tag">{stock.industry}</div>
          </div>
        )}

        <div className="trade-buttons">
          <button
            className="trade-btn buy"
            onClick={() => navigate(`/trade/${symbol}/buy`)}
            data-testid="buy-btn"
          >
            <Plus size={18} strokeWidth={3} /> Buy
          </button>
          {portfolio && portfolio.quantity > 0 && (
            <button
              className="trade-btn sell"
              onClick={() => navigate(`/trade/${symbol}/sell`)}
              data-testid="sell-btn"
            >
              <Minus size={18} strokeWidth={3} /> Sell
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Trade Page
const TradePage = () => {
  const [stock, setStock] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [userBalance, setUserBalance] = useState(0);
  const [ownedShares, setOwnedShares] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const pathParts = location.pathname.split('/');
  const symbol = pathParts[2];
  const action = pathParts[3];

  useEffect(() => {
    fetchData();
  }, [symbol]);

  const fetchData = async () => {
    try {
      const [stockRes, portfolioRes, userRes] = await Promise.all([
        axios.get(`${API}/stocks/${symbol}`),
        axios.get(`${API}/portfolio/${user.user_id}`),
        axios.get(`${API}/users/${user.user_id}`),
      ]);

      setStock(stockRes.data);
      setUserBalance(userRes.data.balance);

      const holding = portfolioRes.data.portfolio.find((p) => p.symbol === symbol);
      setOwnedShares(holding?.quantity || 0);
    } catch (error) {
      console.error('Error fetching trade data:', error);
    } finally {
      setLoading(false);
    }
  };

  const qty = parseInt(quantity) || 0;
  const totalCost = qty * (stock?.price || 0);
  const canAfford = userBalance >= totalCost;
  const canSell = ownedShares >= qty;

  const formatMoney = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleTrade = async () => {
    if (qty < 1) {
      alert('Please enter at least 1 share');
      return;
    }
    if (action === 'buy' && !canAfford) {
      alert(`Not enough balance. Need ${formatMoney(totalCost)} but only have ${formatMoney(userBalance)}`);
      return;
    }
    if (action === 'sell' && !canSell) {
      alert(`Not enough shares. You only own ${ownedShares} shares.`);
      return;
    }

    setExecuting(true);
    try {
      const response = await axios.post(`${API}/trade`, {
        user_id: user.user_id,
        symbol: symbol,
        quantity: qty,
        action: action,
      });

      setResult(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Trade failed. Please try again.');
    } finally {
      setExecuting(false);
    }
  };

  if (loading) return <Layout activeTab="explore"><LoadingScreen /></Layout>;

  if (result) {
    return (
      <Layout activeTab="explore">
        <div className="trade-result-page" data-testid="trade-result-page">
          <div className="result-icon"><Check size={40} strokeWidth={3} /></div>
          <h2 className="result-title">{action === 'buy' ? 'Purchase Complete!' : 'Sale Complete!'}</h2>
          <p className="result-message">{result.message}</p>

          <div className="result-card">
            <div className="result-row">
              <span>Stock</span>
              <span>{symbol}</span>
            </div>
            <div className="result-row">
              <span>Shares</span>
              <span>{qty}</span>
            </div>
            <div className="result-row">
              <span>Price per Share</span>
              <span>{formatMoney(stock.price)}</span>
            </div>
            <div className="result-divider" />
            <div className="result-row">
              <span>Total</span>
              <span className={action === 'buy' ? 'negative' : 'positive'}>
                {action === 'buy' ? '-' : '+'}{formatMoney(totalCost)}
              </span>
            </div>
            {result.profit_loss !== null && result.profit_loss !== undefined && (
              <div className="result-row">
                <span>{result.profit_loss >= 0 ? <><TrendingUp size={15} /> Profit</> : <><TrendingDown size={15} /> Loss</>}</span>
                <span className={result.profit_loss >= 0 ? 'positive' : 'negative'}>
                  {result.profit_loss >= 0 ? '+' : ''}{formatMoney(result.profit_loss)}
                </span>
              </div>
            )}
            <div className="result-row">
              <span>New Balance</span>
              <span>{formatMoney(result.new_balance)}</span>
            </div>
          </div>

          <NewBadgesBanner badges={result.new_badges} />

          {result.ai_feedback && (
            <div className="ai-feedback-card" data-testid="ai-feedback">
              <div className="ai-feedback-header">
                <Sparkles size={18} />
                <span>AI Investment Tips</span>
              </div>
              <p className="ai-feedback-text"><RichText text={result.ai_feedback} /></p>
            </div>
          )}

          <button className="done-btn" onClick={() => navigate('/dashboard')} data-testid="done-btn">
            Done
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout activeTab="explore">
      <div className="trade-page" data-testid="trade-page">
        <div className="trade-header">
          <button className="close-btn" onClick={() => navigate(-1)}><X size={18} /></button>
          <h2 className="trade-title">{action === 'buy' ? 'Buy' : 'Sell'} {symbol}</h2>
          <div style={{ width: 40 }} />
        </div>

        <div className={`action-badge ${action}`}>
          {action === 'buy' ? <ArrowDownToLine size={22} /> : <ArrowUpFromLine size={22} />}
        </div>

        <h3 className="trade-stock-name">{stock?.name}</h3>
        <p className="trade-price">{formatMoney(stock?.price)}/share</p>

        <div className="quantity-section">
          <p className="quantity-label">Number of Shares</p>
          <div className="quantity-controls">
            <button className="qty-btn" onClick={() => setQuantity(Math.max(1, qty - 1).toString())} data-testid="qty-minus-btn"><Minus size={18} /></button>
            <input
              type="number"
              className="qty-input"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="quantity-input"
            />
            <button className="qty-btn" onClick={() => setQuantity((qty + 1).toString())} data-testid="qty-plus-btn"><Plus size={18} /></button>
          </div>
          <div className="quick-qty">
            {[1, 5, 10, 25].map((amount) => (
              <button
                key={amount}
                className={`quick-qty-btn ${qty === amount ? 'active' : ''}`}
                onClick={() => setQuantity(amount.toString())}
              >
                {amount}
              </button>
            ))}
          </div>
        </div>

        <div className="total-section">
          <div className="total-row">
            <span className="total-label">Total Cost</span>
            <span className="total-value">{formatMoney(totalCost)}</span>
          </div>
          {action === 'buy' && (
            <div className="balance-row">
              <span>Your Balance</span>
              <span className={!canAfford ? 'insufficient' : ''}>{formatMoney(userBalance)}</span>
            </div>
          )}
          {action === 'sell' && (
            <div className="balance-row">
              <span>Shares Owned</span>
              <span className={!canSell && qty > 0 ? 'insufficient' : ''}>{ownedShares} shares</span>
            </div>
          )}
          {action === 'buy' && !canAfford && (
            <div className="warning-box">
              <TriangleAlert size={15} /> Not enough balance. Add more money in Settings.
            </div>
          )}
          {action === 'sell' && !canSell && qty > 0 && (
            <div className="warning-box">
              <TriangleAlert size={15} /> You only own {ownedShares} shares of {symbol}.
            </div>
          )}
        </div>

        <button
          className={`execute-trade-btn ${action}`}
          onClick={handleTrade}
          disabled={
            (action === 'buy' && !canAfford) ||
            (action === 'sell' && !canSell) ||
            qty < 1 ||
            executing
          }
          data-testid="execute-trade-btn"
        >
          {executing ? 'Processing...' : `${action === 'buy' ? 'Buy' : 'Sell'} ${qty} Share${qty !== 1 ? 's' : ''}`}
        </button>
      </div>
    </Layout>
  );
};

// History Page
const HistoryPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/transactions/${user.user_id}`);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return <Layout activeTab="history"><LoadingScreen /></Layout>;

  return (
    <Layout activeTab="history">
      <div className="history-page" data-testid="history-page">
        <div className="history-header">
          <h1 className="history-title">Trade History</h1>
          <p className="history-subtitle">{transactions.length} {transactions.length === 1 ? 'trade' : 'trades'} made</p>
        </div>

        {transactions.length === 0 ? (
          <div className="empty-card">
            <span className="empty-emoji"><ScrollText size={40} /></span>
            <h4 className="empty-title">No trades yet!</h4>
            <p className="empty-subtitle">Your buy and sell history will appear here</p>
          </div>
        ) : (
          <div className="transactions-list" data-testid="transactions-list">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="transaction-card"
                onClick={() => setSelectedTransaction(tx)}
                data-testid={`transaction-${tx.id}`}
              >
                <div className="transaction-left">
                  <div className={`action-badge small ${tx.action}`}>
                    {tx.action === 'buy' ? <ArrowDownToLine size={18} /> : <ArrowUpFromLine size={18} />}
                  </div>
                  <div className="transaction-info">
                    <p className="transaction-title">{tx.action === 'buy' ? 'Bought' : 'Sold'} {tx.symbol}</p>
                    <p className="transaction-subtitle">{tx.quantity} shares @ {formatMoney(tx.price_per_share)}</p>
                    <p className="transaction-date">{formatDate(tx.timestamp)}</p>
                  </div>
                </div>
                <div className="transaction-right">
                  <p className={`transaction-amount ${tx.action === 'buy' ? 'negative' : 'positive'}`}>
                    {tx.action === 'buy' ? '-' : '+'}{formatMoney(tx.total_amount)}
                  </p>
                  {tx.profit_loss !== null && (
                    <p className={`profit-loss ${tx.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                      {tx.profit_loss >= 0 ? '+' : ''}{formatMoney(tx.profit_loss)}
                    </p>
                  )}
                  {tx.ai_feedback && (
                    <div className="ai-badge"><Sparkles size={12} /> AI Tips</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedTransaction && (
          <div className="modal-overlay" onClick={() => setSelectedTransaction(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="transaction-modal">
              <div className="modal-header">
                <h3>Trade Details</h3>
                <button className="modal-close" onClick={() => setSelectedTransaction(null)}><X size={18} /></button>
              </div>
              <div className="modal-body">
                <div className={`modal-action-badge ${selectedTransaction.action}`}>
                  {selectedTransaction.action === 'buy' ? <ArrowDownToLine size={22} /> : <ArrowUpFromLine size={22} />}
                </div>
                <h4 className="modal-symbol">{selectedTransaction.symbol}</h4>
                <p className="modal-company">{selectedTransaction.company_name}</p>
                <p className={`modal-amount ${selectedTransaction.action === 'buy' ? 'negative' : 'positive'}`}>
                  {selectedTransaction.action === 'buy' ? '-' : '+'}{formatMoney(selectedTransaction.total_amount)}
                </p>

                <div className="modal-details">
                  <div className="detail-row">
                    <span>Action</span>
                    <span>{selectedTransaction.action === 'buy' ? <><ArrowUpRight size={15} /> Buy</> : <><ArrowDownRight size={15} /> Sell</>}</span>
                  </div>
                  <div className="detail-row">
                    <span>Shares</span>
                    <span>{selectedTransaction.quantity}</span>
                  </div>
                  <div className="detail-row">
                    <span>Price/Share</span>
                    <span>{formatMoney(selectedTransaction.price_per_share)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Date</span>
                    <span>{formatDate(selectedTransaction.timestamp)}</span>
                  </div>
                </div>

                {selectedTransaction.profit_loss !== null && (
                  <div className={`profit-card ${selectedTransaction.profit_loss >= 0 ? 'positive' : 'negative'}`}>
                    <p>{selectedTransaction.profit_loss >= 0 ? <><TrendingUp size={15} /> Profit</> : <><TrendingDown size={15} /> Loss</>}</p>
                    <p className="profit-value">
                      {selectedTransaction.profit_loss >= 0 ? '+' : ''}{formatMoney(selectedTransaction.profit_loss)}
                    </p>
                  </div>
                )}

                {selectedTransaction.ai_feedback && (
                  <div className="ai-feedback-card">
                    <div className="ai-feedback-header">
                      <Sparkles size={18} />
                      <span>AI Investment Insights</span>
                    </div>
                    <p className="ai-feedback-text"><RichText text={selectedTransaction.ai_feedback} /></p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// Settings Page
const SettingsPage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddMoney, setShowAddMoney] = useState(false);
  const [moneyAmount, setMoneyAmount] = useState('');
  const [addingMoney, setAddingMoney] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.user_id}`);
      setUserData(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateMode = async (mode) => {
    try {
      await axios.patch(`${API}/users/${user.user_id}`, { complexity_mode: mode });
      setUserData((prev) => ({ ...prev, complexity_mode: mode }));
    } catch (error) {
      console.error('Error updating mode:', error);
    }
  };

  const handleAddMoney = async () => {
    const amount = parseFloat(moneyAmount);
    if (isNaN(amount) || amount < 1) {
      alert('Please enter a valid amount');
      return;
    }

    setAddingMoney(true);
    try {
      const response = await axios.post(`${API}/users/${user.user_id}/add-money`, { amount });
      setUserData((prev) => ({ ...prev, balance: response.data.new_balance }));
      setShowAddMoney(false);
      setMoneyAmount('');
      alert(`$${amount.toLocaleString()} has been added to your account!`);
    } catch (error) {
      console.error('Error adding money:', error);
      alert('Failed to add money. Please try again.');
    } finally {
      setAddingMoney(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await logout();
      navigate('/login', { replace: true });
    }
  };

  const formatMoney = (value) => {
    return `$${(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) return <Layout activeTab="settings"><LoadingScreen /></Layout>;

  return (
    <Layout activeTab="settings">
      <div className="settings-page" data-testid="settings-page">
        <h1 className="settings-title">Settings</h1>

        <div className="profile-card" data-testid="profile-card">
          <div className="avatar">{userData?.name?.charAt(0) || '?'}</div>
          <h2 className="profile-name">{userData?.name}</h2>
          <p className="profile-email">{userData?.email}</p>
          {userData?.age && <p className="profile-age">{userData.age} years old</p>}
          <div className="balance-tag">
            <Banknote size={16} /> {formatMoney(userData?.balance)}
          </div>
        </div>

        <div className="add-money-card" onClick={() => setShowAddMoney(true)} data-testid="add-money-card">
          <div className="add-money-left">
            <div className="add-money-icon">+</div>
            <div>
              <p className="add-money-title">Add Fake Money</p>
              <p className="add-money-subtitle">Get more money to invest</p>
            </div>
          </div>
          <span className="chevron"><ChevronRight size={18} /></span>
        </div>

        <div className="settings-section">
          <h3 className="section-title">Experience Mode</h3>
          <p className="section-subtitle">Choose how detailed you want the info</p>

          <button
            className={`mode-option ${userData?.complexity_mode === 'simple' ? 'active' : ''}`}
            onClick={() => updateMode('simple')}
            data-testid="mode-option-simple"
          >
            <div className="mode-left">
              <span className="mode-emoji"><Sprout size={26} /></span>
              <div>
                <p className="mode-title">Simple Mode</p>
                <p className="mode-desc">Easy explanations (Ages 8-12)</p>
              </div>
            </div>
            {userData?.complexity_mode === 'simple' && <span className="check"><Check size={18} /></span>}
          </button>

          <button
            className={`mode-option ${userData?.complexity_mode === 'advanced' ? 'active' : ''}`}
            onClick={() => updateMode('advanced')}
            data-testid="mode-option-advanced"
          >
            <div className="mode-left">
              <span className="mode-emoji"><Mountain size={26} /></span>
              <div>
                <p className="mode-title">Advanced Mode</p>
                <p className="mode-desc">Detailed info (Ages 13+)</p>
              </div>
            </div>
            {userData?.complexity_mode === 'advanced' && <span className="check"><Check size={18} /></span>}
          </button>
        </div>

        <div className="settings-section">
          <h3 className="section-title">About Stock Rodeo</h3>
          <div className="info-card">
            <span className="info-emoji"><Logo size={56} /></span>
            <h4 className="info-title">Growing Your Money Skills</h4>
            <p className="info-text">
              Stock Rodeo uses fake money to teach you how the stock market works. 
              All prices are real, but the money isn't - so you can learn without any risk!
            </p>
          </div>
        </div>

        <button className="logout-btn" onClick={handleLogout} data-testid="logout-btn">
          <LogOut size={17} /> Log Out
        </button>

        {showAddMoney && (
          <div className="modal-overlay" onClick={() => setShowAddMoney(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} data-testid="add-money-modal">
              <div className="modal-header">
                <h3>Add Fake Money</h3>
                <button className="modal-close" onClick={() => setShowAddMoney(false)}><X size={18} /></button>
              </div>
              <div className="modal-body center">
                <span className="modal-emoji"><Coins size={44} /></span>
                <p className="modal-subtitle">How much do you want to add?</p>
                <div className="money-input-container">
                  <span className="dollar-sign">$</span>
                  <input
                    type="number"
                    className="money-input"
                    placeholder="0"
                    value={moneyAmount}
                    onChange={(e) => setMoneyAmount(e.target.value)}
                    autoFocus
                    data-testid="add-money-input"
                  />
                </div>
                <div className="quick-amounts">
                  {['1000', '5000', '10000', '25000'].map((amount) => (
                    <button key={amount} className="quick-amount" onClick={() => setMoneyAmount(amount)}>
                      ${parseInt(amount).toLocaleString()}
                    </button>
                  ))}
                </div>
                <button
                  className="add-money-btn"
                  onClick={handleAddMoney}
                  disabled={!moneyAmount || addingMoney}
                  data-testid="confirm-add-money-btn"
                >
                  {addingMoney ? 'Adding...' : 'Add Money'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
      <Route path="/stock/:symbol" element={<ProtectedRoute><StockDetailPage /></ProtectedRoute>} />
      <Route path="/trade/:symbol/:action" element={<ProtectedRoute><TradePage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

const WatchlistBridge = ({ children }) => {
  const { user } = useAuth();
  return <WatchlistProvider userId={user?.onboarded ? user.user_id : null}>{children}</WatchlistProvider>;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WatchlistBridge>
          <AppRouter />
        </WatchlistBridge>
        <Toaster position="bottom-center" offset={96} toastOptions={{ className: 'rodeo-toast' }} />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
